import { useState, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import {
  getStaffQuarterRequests,
  getUserStaffQuarterRequests,
  getEmployeeDetailsForStaffQuarter
} from "../../../../core/data/redux/staffQuarterSlice";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";

/* ================= TYPES ================= */
interface QuarterRequest {
  id: string;
  referenceNumber: string;
  employeeNumber: string;
  employeeName?: string;
  designation?: string;
  date: string;
  status: string;
  quarterTypeEligibility?: {
    id: number;
    quarterType: string;
  };
  approvalDetails?: {
    approverName: string;
    comment: string;
  };
}

interface TableColumn<T> {
  title: string;
  key: string;
  className?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
}

/* ================= COMPONENTS ================= */
const DashboardTile = ({
  title,
  icon,
  count,
  link,
  colorClass = "text-primary",
}: {
  title: string;
  icon?: string;
  count?: string | number;
  link: string;
  colorClass?: string;
}) => {
  return (
    <div className="col-xxl col-xl col-lg-3 col-md-4 col-sm-6 mb-3">
      <div
        className="card h-100 border-0 shadow-sm hover-card"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          cursor: "pointer",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
      >
        <div className="card-body d-flex align-items-center p-3">
          <div
            className={`d-flex align-items-center justify-content-center me-3 rounded-circle`}
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
              border: "1px solid rgba(0,0,0,0.03)"
            }}
          >
            {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
          </div>
          <div>
            <div className="text-muted small mb-1" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>{title}</div>
            <div className={`h4 fw-bold mb-0 ${colorClass}`}>{count}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="mb-4">
      <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </h5>
      <div className="row">{children}</div>
    </div>
  );
};

const StaffQuarterRequests: React.FC = () => {
  const routes = all_routes as any;
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
  let requestsData: QuarterRequest[] = [];
  if (Array.isArray(staffQuarterState?.requests)) {
    requestsData = staffQuarterState.requests;
  } else if (Array.isArray(staffQuarterState?.requests?.data)) {
    requestsData = staffQuarterState.requests.data;
  } else if (Array.isArray(staffQuarterState?.requests?.data?.requests)) {
    requestsData = staffQuarterState.requests.data.requests;
  }
  const employeeDetails = staffQuarterState?.employeeDetails;

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState((location.state as any)?.filterStatus || "All Statuses");

  useEffect(() => {
    if ((location.state as any)?.filterStatus) {
      setStatusFilter((location.state as any).filterStatus);
    }
  }, [location.state]);

  useEffect(() => {
    if (!employeeDetails) {
      dispatch(getEmployeeDetailsForStaffQuarter({}));
    }
  }, [dispatch, employeeDetails]);

  useEffect(() => {
    if (employeeDetails?.employeeNumber) {
      const payload: any = {
        employeeId: employeeDetails.employeeNumber
      };
      if (statusFilter !== "All Statuses") {
        payload.status = statusFilter.toUpperCase();
      }
      dispatch(getUserStaffQuarterRequests(payload));
    }
  }, [dispatch, employeeDetails, statusFilter]);

  const isLoading = staffQuarterState.loading === "pending";

  const filterOptions = useMemo(() => {
    // We can provide hardcoded options if we know them, 
    // or keep deriving from data but maybe add defaults
    const statuses = Array.from(new Set(requestsData.map(r => r.status))).filter(Boolean).sort();
    const uniqueStatuses = Array.from(new Set(["PENDING", "APPROVED", "REJECTED", ...statuses]));
    return {
      statuses: ["All Statuses", ...uniqueStatuses]
    };
  }, [requestsData]);

  const filteredData = useMemo(() => {
    return requestsData.filter(item => {
      const matchesSearch = item.referenceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.employeeNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.employeeName?.toLowerCase().includes(searchTerm.toLowerCase());
      // Since we filter at API level now, the local status filter is secondary or redundant
      // but let's keep it for search-in-results
      const matchesStatus = statusFilter === "All Statuses" || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [requestsData, searchTerm, statusFilter]);

  const stats = useMemo(() => ({
    total: requestsData.length,
    approved: requestsData.filter(r => r.status === "APPROVED" || r.status === "Approved").length,
    pending: requestsData.filter(r => r.status?.toUpperCase() === "UNDER REVIEW" || r.status?.toUpperCase() === "PENDING").length,
    rejected: requestsData.filter(r => r.status === "REJECTED" || r.status === "Rejected").length,
    withdrawal: staffQuarterState?.requests?.data?.counts?.withdrawalRequests || 0
  }), [requestsData, staffQuarterState]);

  const getStatusBadgeClass = (s: string) => {
    switch (s?.toUpperCase()) {
      case "APPROVED": return "badge bg-success";
      case "REJECTED": return "badge bg-danger";
      case "UNDER REVIEW":
      case "PENDING": return "badge bg-warning";
      default: return "badge bg-secondary";
    }
  };

  const formatDate = (date: any) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return (
      <div className="d-flex flex-column">
        <span className="text-dark fw-medium" style={{ fontSize: '13px' }}>
          <i className="ti ti-calendar-event me-1 text-muted"></i>
          {`${day} ${month}, ${year}`}
        </span>
        <span className="text-muted extra-small" style={{ fontSize: '11px' }}>
          <i className="ti ti-clock me-1"></i>
          {`${hours}:${minutes}`}
        </span>
      </div>
    );
  };

  const getValueByPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  /* ---------- CONFIGURATIONS ---------- */
  const dashboardSections = [
    {
      title: "Overview",
      tiles: [
        { title: "Total Requests", icon: "ti-file-text", count: stats.total, link: "#", colorClass: "text-primary" },
        { title: "Approved", icon: "ti-circle-check", count: stats.approved, link: "#", colorClass: "text-success" },
        { title: "Pending Review", icon: "ti-clock", count: stats.pending, link: "#", colorClass: "text-warning" },
        { title: "Rejected", icon: "ti-circle-x", count: stats.rejected, link: "#", colorClass: "text-danger" },
        { title: "Withdrawals", icon: "ti-arrow-back-up", count: stats.withdrawal, link: "#", colorClass: "text-info" },
      ]
    }
  ];

  const columns: TableColumn<QuarterRequest>[] = [
    { title: "Ref Number", key: "referenceNumber", className: "fw-bold text-dark" },
    { title: "Employee Name", key: "employeeName", render: (v) => v || "-" },
    { title: "Employee No", key: "employeeNumber" },
    { title: "Designation", key: "designation", render: (v) => v || "-" },
    { title: "Date", key: "date", render: (v) => formatDate(v) },
    {
      title: "Status",
      key: "status",
      className: "text-center",
      render: (v: string) => <span className={getStatusBadgeClass(v)}>{v}</span>
    },
    {
      title: "Action",
      key: "action",
      className: "text-end",
      render: (_: any, record: QuarterRequest) => (
        <Link to={routes.staffQuarter.details.replace(":id", encodeRouteParam(record.id))} className="btn btn-sm btn-outline-primary py-1 px-2 rounded-6">
          View Details
        </Link>
      )
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Staff Quarter Overview</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard || "#"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Staff Quarter Overview
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to={routes.staffQuarter.unwillingness} className="btn btn-outline-danger">
              <i className="ti ti-ban me-1"></i>
              Unwillingness
            </Link>
            <Link to={routes.staffQuarter.newRegistration} className="btn btn-danger">
              <i className="ti ti-plus me-1"></i>
              New Registration
            </Link>
          </div>
        </div>

        {/* DASHBOARD SECTIONS */}
        {dashboardSections.map((section, sIdx) => (
          <DashboardSection key={sIdx} title={section.title}>
            {section.tiles.map((tile, tIdx) => (
              <DashboardTile
                key={tIdx}
                title={tile.title}
                icon={tile.icon}
                count={tile.count}
                link={tile.link}
                colorClass={tile.colorClass}
              />
            ))}
          </DashboardSection>
        ))}

        <div className="card shadow-sm border-0" style={{ borderRadius: '12px' }}>
          <div className="card-header bg-white py-3 border-bottom-0" style={{ borderRadius: '12px 12px 0 0' }}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
              <h5 className="card-title mb-0">Requests History</h5>
              <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search Ref / Emp..."
                  style={{ maxWidth: 180 }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                  className="form-select form-select-sm w-auto"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {filterOptions.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive" style={{ minHeight: '200px' }}>
              <table className="table table-hover table-center mb-0">
                <thead className="thead-light">
                  <tr>
                    {columns.map((col, idx) => (
                      <th key={idx} className={col.className || ""}>{col.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-5">
                        <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                        Loading requests...
                      </td>
                    </tr>
                  ) : filteredData.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-4 text-muted">
                        No requests found matching your filters
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((record, idx) => (
                      <tr key={idx}>
                        {columns.map((col, cIdx) => (
                          <td key={cIdx} className={col.className || ""}>
                            {col.render ? col.render(getValueByPath(record, col.key), record, idx) : String(getValueByPath(record, col.key) || "-")}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <style>{`
        .bg-primary-light { background-color: rgba(var(--bs-primary-rgb), 0.08); }
        .rounded-6 { border-radius: 6px; }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
      `}</style>
      </div>
    </div>
  );
};

export default StaffQuarterRequests;
